import { useEffect, useRef } from 'react';
import { SUBJECT_VOCABULARY_BY_VERTICAL, hasStatusChoice, type SubjectStatus } from '@ni/shared';
import {
  PROMOTE_ACTION,
  PROMOTE_CANCEL,
  PROMOTE_WORKING,
  promoteControlLabel,
  subjectStatusLabel,
} from '../../lib/copy';
import { ErrorLine } from '../ui/error-line';

/**
 * **The one word a vertical files everyone under**, or `null` where the office
 * is asked to choose (personal-vertical note §7). `null` is the club and the
 * business: there is a picker, and it has exits.
 */
export const onlyWordFor = (vertical: string): SubjectStatus | null =>
  hasStatusChoice(vertical) ? null : (SUBJECT_VOCABULARY_BY_VERTICAL[vertical]?.live[0] ?? null);

/**
 * **Whether the picker's two global closers are listening** — the outside
 * mousedown and the Escape key.
 *
 * They may listen only while a picker is actually on the page, and on a one-word
 * vertical there is never one: Track writes straight through, and the branch
 * that does renders no `promote-open` element for `root` to point at. Left
 * running there, both closers misfire on a `root.current` that is `null` — every
 * mousedown reads as "outside", so the first click anywhere after a refused
 * create takes the refusal text off the screen before it has been read, and
 * Escape is swallowed here instead of reaching the route that owns it
 * (`routes/knowledge.tsx`, whose own Escape closes the panel).
 *
 * Exported because the SPA suite has no DOM and no test-only dependency — the
 * house rule stated in `routes/people-search.spec.tsx` — so the guard is pinned
 * the way that rule leaves open, as the shipped expression fed the shipped
 * derivation, rather than by pressing a key that nothing here can press.
 */
export const pickerExitsLive = (open: boolean, onlyWord: SubjectStatus | null): boolean =>
  open && onlyWord === null;

/**
 * **The adopt door** (import note §1.3): the one gesture a known person's row
 * carries. Choosing a word gives them a subject record and carries the entity —
 * every fact already on it — across; nothing is minted and nothing is copied.
 *
 * It lives in its own file because it is now offered in two places. It was the
 * People table's, and the knowledge base lists known people too (design note
 * 2026-09-07 §1.3) — where the same door must behave the same way down to the
 * key that closes it. Two copies of a control with this much keyboard behaviour
 * in it would be two copies that drift.
 *
 * The picker offers the vertical's **live** words only — a word the server
 * would refuse is not a choice — and it stays open while the write is in the
 * air, so the reader can see which word they are waiting on.
 *
 * **It has three ways out, and it needed them.** Opening it replaces the Track
 * button with the words, so the only exits were choosing one — an irreversible
 * write on somebody's record — and opening a *different* row's picker. A reader
 * who pressed Track to see what the words were had nothing to press to mean "not
 * this person after all", which is the state a control must never leave anyone
 * in. So: a Cancel beside the words, Escape, and a click anywhere else. All
 * three are `onOpen(null)`, which is the same close the route already had.
 *
 * The two that are the reader *backing out* — Cancel and Escape — put the caret
 * back on the Track button they opened, the same rule the record head's status
 * control keeps. A click elsewhere has already chosen somewhere else to be, and
 * moving focus out from under it would be the app arguing.
 *
 * None of the three is offered while the write is in the air: the words are
 * frozen then, and an exit that leaves a create running would say the reader had
 * stopped something they had not.
 *
 * **On a vertical with one word there is no picker and no exits to give it**
 * (personal-vertical note §7). Track writes the one word straight through, which
 * is what the picker would have done after one more press and one more decision
 * the reader had no information to make. Everything above still describes the
 * only door that has a choice in it; what stays true either way is that Track is
 * the irreversible gesture and it is the reader who presses it.
 */
export function TrackDoor({
  vertical,
  name,
  entityId,
  open,
  busy,
  error,
  onOpen,
  onChoose,
}: {
  /** The vertical whose words the picker offers — the reader's own org's. */
  vertical: string;
  /** Whose picker this is, for the group's accessible name. */
  name: string;
  /** What the route's `openFor` is keyed on: a known person has no other id. */
  entityId: string;
  open: boolean;
  busy: boolean;
  /** A failed promotion, reported under the row that asked for it. */
  error: unknown;
  onOpen: (entityId: string | null) => void;
  onChoose: (status: SubjectStatus) => void;
}) {
  const vocabulary = SUBJECT_VOCABULARY_BY_VERTICAL[vertical];
  /** The one word, when the vertical has exactly one — what Track then writes. */
  const onlyWord = onlyWordFor(vertical);

  const root = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  /** Whether the close now in flight should hand the caret back to the trigger. */
  const returnFocus = useRef(false);

  /* Held in a ref rather than closed over by the listeners: the route's state is
     a fresh object on every render — and the screen re-renders on every keystroke
     in the search box — so a listener bound to it would be torn down and rebuilt
     for each one. */
  const close = useRef<(focusTrigger: boolean) => void>(() => {});
  useEffect(() => {
    close.current = (focusTrigger) => {
      if (busy) return;
      returnFocus.current = focusTrigger;
      onOpen(null);
    };
  });

  useEffect(() => {
    if (!pickerExitsLive(open, onlyWord)) return;
    const onKey = (event: KeyboardEvent) => {
      // A dialog's Escape is the dialog's — the rule every other layer keeps.
      if (document.querySelector('[aria-modal="true"]')) return;
      if (event.key === 'Escape') close.current(true);
    };
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) close.current(false);
    };

    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, onlyWord]);

  /* The trigger unmounts while the picker is up, so the caret cannot be put back
     until it exists again — which is this render. Only a departure that asked
     for it moves focus; a successful promotion navigates away and must not have
     the caret snatched back on its way out.

     On the one-word path there is no picker to leave and no trigger that ever
     unmounted, so this stands down with the closers. It cannot be guarded on
     `pickerExitsLive` itself: this effect's whole job is the render where `open`
     has just become false, which is the render where the exits are *not* live. */
  useEffect(() => {
    if (onlyWord !== null) return;
    if (open || !returnFocus.current) return;
    returnFocus.current = false;
    trigger.current?.focus();
  }, [open, onlyWord]);

  /* A vertical this build does not carry has no words to offer, so there is no
     door — the same call the intake form makes (verticals note §0.3). */
  if (!vocabulary) return null;

  /* One word: the button is the whole door. There is no picker to put up, so
     none of its three exits applies — Cancel is not rendered, and the two that
     listen globally are stood down above rather than merely left unrendered.
     Unrendering alone was the bug: `open` still goes true here, so the closers
     were live over a `root` pointing at nothing, and the first mousedown after a
     refusal closed a picker that was never up and took the reason with it.

     It still calls `onOpen` before it writes, and that is not bookkeeping. The
     route holds **one** create mutation for the whole list, so `error` is the
     same value under every known row; `open` is what says which row asked, and
     it is the only reason a refusal here lands under the name it was about
     rather than under all of them. It also resets a previous refusal, which is
     what the picker's own Track press does. */
  if (onlyWord !== null) {
    return (
      <>
        <button
          type="button"
          className="quiet-action promote"
          ref={trigger}
          aria-busy={busy}
          onClick={() => {
            /* `aria-busy` rather than `disabled`, the same call the picker's own
               trigger makes: a disabled button cannot hold focus, and dropping
               the reader on `document.body` for the length of a create is worse
               than a second press the route already ignores. */
            if (busy) return;
            onOpen(entityId);
            onChoose(onlyWord);
          }}
        >
          {busy ? PROMOTE_WORKING : PROMOTE_ACTION}
        </button>
        {open && error ? <ErrorLine error={error} /> : null}
      </>
    );
  }

  return (
    <>
      {open ? (
        <span className="promote-open" ref={root}>
          <span className="promote-picker" role="group" aria-label={promoteControlLabel(name)}>
            {vocabulary.live.map((word) => (
              <button
                key={word}
                type="button"
                className="seg-option"
                disabled={busy}
                onClick={() => onChoose(word)}
              >
                {subjectStatusLabel(vertical, word)}
              </button>
            ))}
          </span>
          {/* Outside the group: Cancel is not one of the words, and a reader
              hearing "Track Marion East as: Member, Contact, Cancel" would be
              offered a status the club does not have. */}
          <button
            type="button"
            className="quiet-action promote"
            disabled={busy}
            onClick={() => close.current(true)}
          >
            {PROMOTE_CANCEL}
          </button>
        </span>
      ) : (
        <button
          type="button"
          className="quiet-action promote"
          ref={trigger}
          aria-busy={busy}
          onClick={() => onOpen(entityId)}
        >
          {busy ? PROMOTE_WORKING : PROMOTE_ACTION}
        </button>
      )}

      {/* A refused promotion leaves the picker up with the reason under it: the
          words are still there, and one of them may be the one to try. */}
      {open && error ? <ErrorLine error={error} /> : null}
    </>
  );
}
